// D73 — Page /stocks : liste articles + CRUD complet (P27b)
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { articlesApi, stocksApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { useT } from "@/lib/i18n";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCan } from "@/hooks/useCan";
import { Trash2, Pencil, Plus, History } from "lucide-react";
import { ht } from "@/lib/ht";

export default function ListeArticlesPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const t = useT();
  const canWrite = useCan("write");
  const canDelete = useCan("delete");

  const { data: articles, isLoading } = useQuery({
    queryKey: ["articles"],
    queryFn: () => articlesApi.list(),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState<import("@/types/article").Article | null>(null);
  const [del, setDel] = useState<import("@/types/article").Article | null>(null);
  const [movementModal, setMovementModal] = useState<number | null>(null);
  const [historyModal, setHistoryModal] = useState<number | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => articlesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["articles"] });
      toast({ title: t("articles.toast.deleted"), variant: "success" });
      setDel(null);
    },
    onError: (err) => {
      const msg = (err as Error).message;
      // Si message commence par "Impossible de supprimer", on l'affiche direct
      // ⚠️ le message backend commence par "Impossible de supprimer" ;
      //    le TITRE du toast est "Suppression impossible" (2 clés distinctes).
      const title = msg.startsWith(t("articles.error.delete_impossible"))
        ? t("articles.toast.delete_impossible")
        : t("articles.toast.delete_error");
      toast({
        title,
        description: msg,
        variant: "error",
        duration: 8000,
      });
      setDel(null);
    },
  });

  if (isLoading) return <div className="p-8">{ht("Chargement...")}</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Stocks")}</h1>
          <p className="text-muted-foreground">{ht("Module Articles + mouvements")}</p>
        </div>
        {canWrite && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" />{ht("Nouvel article")}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Articles ({articles?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">{ht("Référence")}</th>
                <th className="px-3 py-2">{t("articles.field.designation")}</th>
                <th className="px-3 py-2">{ht("Nature")}</th>
                <th className="px-3 py-2">{ht("Unité")}</th>
                <th className="px-3 py-2">{ht("Prix unit.")}</th>
                <th className="px-3 py-2">{ht("Qté initiale")}</th>
                <th className="px-3 py-2">{ht("Stock actuel")}</th>
                <th className="px-3 py-2">{ht("Min/Max")}</th>
                <th className="px-3 py-2">{ht("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {articles?.map((a) => (
                <ArticleRow
                  key={a.id}
                  article={a}
                  onMovement={() => setMovementModal(a.id)}
                  onHistory={() => setHistoryModal(a.id)}
                  onEdit={() => setEdit(a)}
                  onDelete={() => setDel(a)}
                  canEdit={canWrite}
                  canDelete={canDelete}
                />
              ))}
            </tbody>
          </table></div>
        </CardContent>
      </Card>

      {showCreate && <CreateArticleModal onClose={() => setShowCreate(false)} />}
      {edit && (
        <EditArticleModal article={edit} onClose={() => setEdit(null)} />
      )}
      {movementModal !== null && (
        <MovementModal
          articleId={movementModal}
          onClose={() => setMovementModal(null)}
          onCreated={() => qc.invalidateQueries({ queryKey: ["articles"] })}
        />
      )}
      {historyModal !== null && (
        <HistoryModal
          articleId={historyModal}
          onClose={() => setHistoryModal(null)}
        />
      )}

      <ConfirmDialog
        open={del !== null}
        title={ht("Supprimer cet article ?")}
        description={`Cette action supprimera définitivement l'article "${del?.reference ?? ""}" ainsi que tous ses mouvements de stock. Irréversible.`}
        confirmLabel={ht("Supprimer")}
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => del && deleteMutation.mutate(del.id)}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}

function ArticleRow({
  article,
  onMovement,
  onHistory,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}: {
  article: import("@/types/article").Article;
  onMovement: () => void;
  onHistory: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
}) {
  const t = useT();
  const { data: stock } = useQuery({
    queryKey: ["stock", article.id],
    queryFn: () => stocksApi.current(article.id),
  });
  const stockActuel = stock?.stock_actuel ?? article.qte_initiale;
  const stockBas = stockActuelToMinMax(stockActuel, article.stock_min);

  return (
    <tr className="border-b hover:bg-muted/30">
      <td className="px-3 py-2 font-mono font-semibold">{article.reference}</td>
      <td className="px-3 py-2">{article.designation}</td>
      <td className="px-3 py-2">
        <span
          className={
            article.nature === "consommable"
              ? "text-blue-700"
              : article.nature === "durable"
                ? "text-green-700"
                : "text-orange-700"
          }
        >
          {article.nature}
        </span>
      </td>
      <td className="px-3 py-2">{article.unite}</td>
      <td className="px-3 py-2 text-right">
        {article.prix_unitaire_fcfa.toLocaleString("fr-FR")} FCFA
      </td>
      <td className="px-3 py-2 text-right">{article.qte_initiale}</td>
      <td className={"px-3 py-2 text-right font-semibold " + (stockBas ? "text-red-600" : "")}>
        {stockActuel.toFixed(2)}
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        {article.stock_min} / {article.stock_max}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={onMovement} title={ht("Mouvement stock")}>
            <Plus className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onHistory} title={ht("Voir historique")}>
            <History className="h-3 w-3" />
          </Button>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={onEdit} title={t("articles.action.edit")}>
              <Pencil className="h-3 w-3" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
              title={ht("Supprimer")}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function stockActuelToMinMax(stock: number, min: number): boolean {
  return stock < min;
}

function CreateArticleModal({ onClose }: { onClose: () => void }) {
  const { toast } = useToast();
  const t = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    reference: "",
    designation: "",
    nature: "consommable",
    unite: "pièce",
    prix_unitaire_fcfa: 0,
    qte_initiale: 0,
    stock_min: 0,
    stock_max: 0,
  });

  const create = useMutation({
    mutationFn: () => articlesApi.create({ ...form, famille_id: null, observation: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["articles"] });
      onClose();
      toast({ title: t("articles.toast.created"), variant: "success" });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(28rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{ht("Nouvel article")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Input label={ht("Référence")} value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} />
            <Input label={t("articles.field.designation")} value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs">{ht("Nature")}</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.nature}
                  onChange={(e) => setForm({ ...form, nature: e.target.value })}
                >
                  <option value="consommable">{ht("Consommable")}</option>
                  <option value="durable">{ht("Durable")}</option>
                  <option value="outillage">{ht("Outillage")}</option>
                </select>
              </div>
              <div>
                <label className="text-xs">{ht("Unité")}</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.unite}
                  onChange={(e) => setForm({ ...form, unite: e.target.value })}
                >
                  <option value="pièce">{ht("Pièce")}</option>
                  <option value="litre">{ht("Litre")}</option>
                  <option value="kg">{ht("Kg")}</option>
                  <option value="mètre">{ht("Mètre")}</option>
                </select>
              </div>
            </div>
            <Input label={ht("Prix unitaire (FCFA)")} type="number" value={form.prix_unitaire_fcfa.toString()} onChange={(v) => setForm({ ...form, prix_unitaire_fcfa: Number(v) })} />
            <div className="grid grid-cols-3 gap-2">
              <Input label={ht("Qté initiale")} type="number" value={form.qte_initiale.toString()} onChange={(v) => setForm({ ...form, qte_initiale: Number(v) })} />
              <Input label={ht("Min")} type="number" value={form.stock_min.toString()} onChange={(v) => setForm({ ...form, stock_min: Number(v) })} />
              <Input label={ht("Max")} type="number" value={form.stock_max.toString()} onChange={(v) => setForm({ ...form, stock_max: Number(v) })} />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={create.isPending}>{ht("Annuler")}</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.reference || !form.designation}>
              {create.isPending ? "En cours..." : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EditArticleModal({
  article,
  onClose,
}: {
  article: import("@/types/article").Article;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const t = useT();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    reference: article.reference,
    designation: article.designation,
    nature: article.nature,
    unite: article.unite,
    prix_unitaire_fcfa: article.prix_unitaire_fcfa,
    qte_initiale: article.qte_initiale,
    stock_min: article.stock_min,
    stock_max: article.stock_max,
  });

  const update = useMutation({
    mutationFn: () => articlesApi.update(article.id, { ...form, famille_id: article.famille_id, observation: article.observation }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["articles"] });
      onClose();
      toast({ title: t("articles.toast.updated"), variant: "success" });
    },
    onError: (err) => {
      toast({
        title: "Erreur",
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(28rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>Modifier {article.reference}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Input label={ht("Référence")} value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} />
            <Input label={t("articles.field.designation")} value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs">{ht("Nature")}</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.nature}
                  onChange={(e) => setForm({ ...form, nature: e.target.value })}
                >
                  <option value="consommable">{ht("Consommable")}</option>
                  <option value="durable">{ht("Durable")}</option>
                  <option value="outillage">{ht("Outillage")}</option>
                </select>
              </div>
              <div>
                <label className="text-xs">{ht("Unité")}</label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.unite}
                  onChange={(e) => setForm({ ...form, unite: e.target.value })}
                >
                  <option value="pièce">{ht("Pièce")}</option>
                  <option value="litre">{ht("Litre")}</option>
                  <option value="kg">{ht("Kg")}</option>
                  <option value="mètre">{ht("Mètre")}</option>
                </select>
              </div>
            </div>
            <Input label={ht("Prix unitaire (FCFA)")} type="number" value={form.prix_unitaire_fcfa.toString()} onChange={(v) => setForm({ ...form, prix_unitaire_fcfa: Number(v) })} />
            <div className="grid grid-cols-3 gap-2">
              <Input label={ht("Qté initiale")} type="number" value={form.qte_initiale.toString()} onChange={(v) => setForm({ ...form, qte_initiale: Number(v) })} />
              <Input label={ht("Min")} type="number" value={form.stock_min.toString()} onChange={(v) => setForm({ ...form, stock_min: Number(v) })} />
              <Input label={ht("Max")} type="number" value={form.stock_max.toString()} onChange={(v) => setForm({ ...form, stock_max: Number(v) })} />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={update.isPending}>{ht("Annuler")}</Button>
            <Button onClick={() => update.mutate()} disabled={update.isPending || !form.reference || !form.designation}>
              {update.isPending ? "En cours..." : "Modifier"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="text-xs">{label}</label>
      <input
        type={type}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function MovementModal({
  articleId,
  onClose,
  onCreated,
}: {
  articleId: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const t = useT();
  const [type, setType] = useState<"entree" | "sortie" | "ajustement">("entree");
  const [quantite, setQuantite] = useState(0);
  const [motif, setMotif] = useState("");

  const create = useMutation({
    mutationFn: () =>
      stocksApi.createMovement(articleId, {
        type,
        quantite,
        motif: motif || undefined,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      toast({ title: t("articles.toast.movement_created"), description: `${type} ${quantite}`, variant: "success" });
    },
    onError: (err) => {
      toast({
        title: ht("Erreur mouvement"),
        description: (err as Error).message,
        variant: "error",
      });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(24rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>Nouveau mouvement (article #{articleId})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div>
              <label className="text-xs">{ht("Type")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as "entree" | "sortie" | "ajustement")}
              >
                <option value="entree">{ht("Entrée")}</option>
                <option value="sortie">{ht("Sortie")}</option>
                <option value="ajustement">{ht("Ajustement")}</option>
              </select>
            </div>
            <Input label={ht("Quantité")} type="number" value={quantite.toString()} onChange={(v) => setQuantite(Number(v))} />
            <Input label={ht("Motif (optionnel)")} value={motif} onChange={setMotif} />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={create.isPending}>{ht("Annuler")}</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || quantite === 0}>
              {create.isPending ? "En cours..." : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function HistoryModal({
  articleId,
  onClose,
}: {
  articleId: number;
  onClose: () => void;
}) {
  const { data: movements, isLoading } = useQuery({
    queryKey: ["movements", articleId],
    queryFn: () => stocksApi.movements(articleId),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>
            Historique article #{articleId} ({movements?.length ?? 0} mouvements)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{ht("Chargement...")}</div>
          ) : !movements || movements.length === 0 ? (
            <div className="text-sm text-muted-foreground">{ht("Aucun mouvement enregistré.")}</div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-2 py-1">{ht("Date")}</th>
                  <th className="px-2 py-1">{ht("Type")}</th>
                  <th className="px-2 py-1 text-right">{ht("Quantité")}</th>
                  <th className="px-2 py-1">{ht("Motif")}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m.id} className="border-b">
                    <td className="px-2 py-1 text-xs">
                      {new Date(m.created_at).toLocaleString("fr-FR")}
                    </td>
                    <td className="px-2 py-1">
                      <span
                        className={
                          m.type === "entree"
                            ? "text-green-700 font-semibold"
                            : m.type === "sortie"
                              ? "text-red-700 font-semibold"
                              : "text-orange-700 font-semibold"
                        }
                      >
                        {m.type}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right font-mono">{m.quantite}</td>
                    <td className="px-2 py-1 text-xs">{m.motif ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={onClose}>{ht("Fermer")}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
